/**
 * Dependency-free caption evidence analysis shared by Node, web and native.
 * Scores are heuristic/provider evidence strengths, never calibrated emotion
 * probabilities. Text is only inspected; transcript words are not rewritten.
 */
const rules = require('./caption-tone-rules.json');
const CAPTION_TONE_DICTIONARY_VERSION = rules.version;
const TEXT_FLOOR = rules.thresholds.textRuleConfidenceFloor;
const HUME_FLOOR = rules.thresholds.humeConfidenceFloor;
const HUME_MARGIN = rules.thresholds.humeCandidateMargin;
const TONES = new Set(Object.keys(rules.toneAccessibilityLabels));
const SYMBOLS = new Set(['!', '?', '~', '...', '>>']);
const NEGATION_PREFIX = new RegExp(rules.textNegationPrefixPattern, 'i');
const REPORTED_PREFIX = new RegExp(rules.textReportedPrefixPattern, 'i');

/** @param {unknown} value @returns {value is import('./caption-tone-analysis').CaptionTone} */
function isTone(value) { return typeof value === 'string' && TONES.has(value); }
/** @param {unknown} value @returns {value is import('./caption-tone-analysis').CaptionVisualCue} */
function isVisualCue(value) {
    return value === 'color' || value === 'font' || value === 'motion' || value === 'spacing' || value === 'tone-lane';
}
/** @param {unknown} value @returns {import('./caption-tone-analysis').CaptionSentiment} */
function sentiment(value) { return value === 'positive' || value === 'negative' ? value : 'neutral'; }
/** @param {number} value */
function clamp01(value) { return Number.isFinite(value) ? Math.min(Math.max(value, 0), 1) : 0; }
/** @param {unknown} value @returns {value is number} */
function validScore(value) {
    return typeof value === 'number' && Number.isFinite(value) && value >= 0 && value <= 1;
}
/** @param {unknown} value @returns {value is Record<string, unknown>} */
function isRecord(value) { return value !== null && typeof value === 'object' && !Array.isArray(value); }
/** @param {unknown} value @returns {value is string} */
function isSymbol(value) { return typeof value === 'string' && SYMBOLS.has(value); }
/** @param {number | null | undefined} value */
function providerWeight(value) {
    // Absence leaves a text-only heuristic unweighted; it is not a measurement.
    return typeof value === 'number' ? clamp01(value > 1 ? value / 100 : value) : 1;
}
/** @param {unknown} value */
function normalizeExpressionLabel(value) {
    return typeof value === 'string' ? value.slice(0, 80).toLowerCase().replace(/[^a-z]+/g, ' ').trim() : '';
}

const DICTIONARY = rules.dictionary.map(entry => {
    if (!isTone(entry.tone) || entry.tone === 'neutral') throw new Error('Invalid caption dictionary tone');
    return { ...entry, tone: entry.tone, sentiment: sentiment(entry.sentiment), visualCues: entry.visualCues.filter(isVisualCue) };
});
const TEXT_RULES = rules.textRules.map(rule => {
    if (!isTone(rule.tone) || rule.tone === 'neutral') throw new Error('Invalid caption text-rule tone');
    return { ...rule, tone: rule.tone, sentiment: sentiment(rule.sentiment), weights: rule.weights.map(weight => ({
        ...weight, pattern: new RegExp(weight.pattern, [...new Set((weight.flags || '') + 'g')].join(''))
    })) };
});
/** @param {string} label */
function findDictionaryEntry(label) {
    const name = normalizeExpressionLabel(label);
    return DICTIONARY.find(entry => entry.labels.includes(name));
}
/** @param {unknown} expressions @returns {import('./caption-tone-analysis').ICaptionHumeExpressionSignal[]} */
function normalizeHumeExpressions(expressions) {
    if (!Array.isArray(expressions)) return [];
    /** @type {import('./caption-tone-analysis').ICaptionHumeExpressionSignal[]} */
    const result = [];
    for (const item of expressions) {
        if (!isRecord(item) || typeof item.name !== 'string' || !validScore(item.score)) continue;
        result.push({ name: normalizeExpressionLabel(item.name), score: item.score });
    }
    return result.sort((a, b) => b.score - a.score);
}
/** @param {unknown} value @returns {import('./caption-tone-analysis').ICaptionProsodySignals | undefined} */
function normalizeProsody(value) {
    if (!isRecord(value)) return undefined;
    /** @type {import('./caption-tone-analysis').ICaptionProsodySignals} */
    const result = {};
    for (const key of ['emphasis', 'pauseMs', 'pitch', 'rate', 'volume']) {
        const number = value[key];
        if (typeof number !== 'number' || !Number.isFinite(number) || number < 0) continue;
        if ((key === 'pitch' || key === 'rate') && number === 0) continue;
        if (key === 'emphasis') result.emphasis = clamp01(number);
        else if (key === 'pauseMs') result.pauseMs = number;
        else if (key === 'pitch') result.pitch = number;
        else if (key === 'rate') result.rate = number;
        else result.volume = number;
    }
    return Object.keys(result).length ? result : undefined;
}
/** @param {import('./caption-tone-analysis').CaptionTone} tone @param {import('./caption-tone-analysis').ICaptionProsodySignals | undefined} prosody */
function prosodyBoost(tone, prosody) {
    if (!prosody) return 0;
    if (tone === 'hesitation' && (prosody.pauseMs || 0) > 500) return 0.22;
    if (['anger', 'emphasis', 'surprise'].includes(tone) && (prosody.emphasis || 0) > 0.6) return 0.2;
    if (tone === 'sadness' && typeof prosody.rate === 'number' && prosody.rate < 0.8) return 0.16;
    if (tone === 'joy' && typeof prosody.pitch === 'number' && prosody.pitch > 1.1) return 0.14;
    return 0;
}
/** @param {string} text @param {{pattern: RegExp, lexical: boolean}} weight */
function matchesTextEvidence(text, weight) {
    weight.pattern.lastIndex = 0;
    let match;
    while ((match = weight.pattern.exec(text))) {
        // A bounded, clause-local English guard: later affirmative clauses can
        // still supply a cue. This is not a general semantic/Arabic classifier.
        const prefix = text.slice(Math.max(0, match.index - 96), match.index);
        const clausePrefix = prefix.split(/\b(?:but|however|yet)\b|[.!?;:\n]/i).pop() || '';
        // A report or definition describes words/another person; it is not
        // evidence of this speaker's delivery. This deliberately abstains in
        // bounded English contexts, not a claim of semantic understanding.
        if (!weight.lexical || (!NEGATION_PREFIX.test(prefix) && !REPORTED_PREFIX.test(clausePrefix))) return true;
        if (!match[0]) break;
    }
    return false;
}
/** @param {string} text @param {number | null | undefined} confidence @param {import('./caption-tone-analysis').ICaptionProsodySignals | undefined} prosody */
function textCandidate(text, confidence, prosody) {
    /** @type {{score: number, tone: import('./caption-tone-analysis').CaptionTone, sentiment: import('./caption-tone-analysis').CaptionSentiment, symbols: string[]}} */
    let best = { score: 0, tone: 'neutral', sentiment: 'neutral', symbols: [] };
    /** @type {Set<import('./caption-tone-analysis').CaptionSentiment>} */
    const sentiments = new Set();
    for (const rule of TEXT_RULES) {
        const matched = rule.weights.filter(weight => matchesTextEvidence(text, weight));
        const lexical = matched.some(weight => weight.lexical);
        const score = matched.reduce((sum, weight) => sum + weight.weight, 0) + (lexical ? prosodyBoost(rule.tone, prosody) : 0);
        if (lexical && score * providerWeight(confidence) >= TEXT_FLOOR && rule.sentiment !== 'neutral') sentiments.add(rule.sentiment);
        if (score > best.score) best = { score, tone: rule.tone, sentiment: rule.sentiment, symbols: rule.symbols };
    }
    return { ...best, conflictingSentiment: sentiments.size > 1, confidence: clamp01(best.score * providerWeight(confidence)) };
}
/** @param {number} confidence @returns {import('./caption-tone-analysis').CaptionToneIntensity} */
function intensity(confidence) { return confidence >= 0.78 ? 'high' : confidence >= 0.58 ? 'medium' : 'low'; }
/** @param {Partial<import('./caption-tone-analysis').ICaptionToneProfile> | undefined} profile */
function getCaptionToneAccessibilityLabel(profile) {
    if (!profile || !isTone(profile.tone) || profile.tone === 'neutral'
        || profile.fusionDecision === 'conflict' || !validScore(profile.confidence) || Number(profile.confidence) < TEXT_FLOOR) return undefined;
    return `possible ${rules.toneAccessibilityLabels[profile.tone]}`;
}
/** @param {import('./caption-tone-analysis').ICaptionToneInput} input @returns {import('./caption-tone-analysis').ICaptionToneProfile} */
/** @param {unknown} value @returns {{ tone: import('./caption-tone-analysis').CaptionTone, sentiment: import('./caption-tone-analysis').CaptionSentiment, confidence: number } | undefined} */
function normalizeSemanticTone(value) {
    if (!isRecord(value) || !isTone(value.tone) || !validScore(value.confidence)) return undefined;
    return { confidence: value.confidence, sentiment: sentiment(value.sentiment), tone: value.tone };
}
const SOCIAL_STANCES = new Set([ 'hostile', 'ironic', 'neutral', 'vulnerable', 'warm' ]);
/** @param {unknown} value @param {number} min @param {number} max */
function boundedNumber(value, min, max) {
    return typeof value === 'number' && Number.isFinite(value) && value >= min && value <= max ? value : undefined;
}
/**
 * Validate continuous tone dimensions from the server. Anything malformed is
 * dropped rather than clamped: a forged or stale field must not restyle text.
 * @param {unknown} value @returns {import('./caption-tone-analysis').ICaptionToneDimensionScores | undefined}
 */
function normalizeCaptionToneDimensions(value) {
    if (!isRecord(value)) return undefined;
    const valence = boundedNumber(value.valence, -1, 1);
    const energy = boundedNumber(value.energy, 0, 1);
    if (valence === undefined || energy === undefined) return undefined;
    const stance = typeof value.socialStance === 'string' && SOCIAL_STANCES.has(value.socialStance)
        ? /** @type {import('./caption-tone-analysis').CaptionToneSocialStance} */ (value.socialStance)
        : 'neutral';
    const sources = Array.isArray(value.sources) ? value.sources.filter(source => source === 'audio' || source === 'text') : [];
    /** @type {import('./caption-tone-analysis').ICaptionToneDimensionScores} */
    const dimensions = {
        energy, socialStance: stance,
        socialStanceConfidence: boundedNumber(value.socialStanceConfidence, 0, 1) ?? 0,
        sources: sources.length ? [ ...new Set(sources) ] : [ 'text' ],
        valence
    };
    const certainty = boundedNumber(value.certainty, 0, 1);
    const urgency = boundedNumber(value.urgency, 0, 1);
    const surprise = boundedNumber(value.surprise, 0, 1);
    const speakerOwnDelivery = boundedNumber(value.speakerOwnDelivery, 0, 1);
    if (certainty !== undefined) dimensions.certainty = certainty;
    if (urgency !== undefined) dimensions.urgency = urgency;
    if (surprise !== undefined) dimensions.surprise = surprise;
    if (speakerOwnDelivery !== undefined) dimensions.speakerOwnDelivery = speakerOwnDelivery;
    const smoothedOver = boundedNumber(value.smoothedOver, 1, 1000);
    if (smoothedOver !== undefined) dimensions.smoothedOver = Math.round(smoothedOver);
    return dimensions;
}
/** @param {import('./caption-tone-analysis').CaptionTone} tone */
function symbolsForTone(tone) {
    if (tone === 'neutral') return [];
    const rule = TEXT_RULES.find(entry => entry.tone === tone);
    if (rule) return rule.symbols;
    const entry = DICTIONARY.find(item => item.tone === tone);
    return entry ? entry.symbols : [];
}
/** @param {import('./caption-tone-analysis').ICaptionToneInput} input @returns {import('./caption-tone-analysis').ICaptionToneProfile} */
function analyzeCaptionTone({ humeExpressions, providerConfidence, prosody, semanticDimensions, semanticTone, text }) {
    const dimensions = normalizeCaptionToneDimensions(semanticDimensions);
    const words = typeof text === 'string' ? text.trim() : '';
    const measured = normalizeProsody(prosody);
    // Ignore paired double/curly/guillemet quotations only for text analysis.
    // Preserve offsets and the original words; ASR text without these marks
    // and more complex reported speech still require language-aware evidence.
    const unquotedWords = words.replace(/"[^"]*"|“[^”]*”|«[^»]*»/g, quote => ' '.repeat(quote.length));
    // A punctuated question asks about words or a state; those words alone do
    // not establish the speaker's delivery. Mask only that punctuation-delimited
    // span, including mixed ?!/!? runs and the Arabic question mark. Preserve
    // neighbouring statements and offsets; voice evidence is evaluated below.
    // Consume unterminated text and other boundaries too, avoiding repeated
    // scans of long unpunctuated input. Missing/misrecognized punctuation still
    // requires real speech evaluation.
    const evidenceWords = unquotedWords.replace(/[^.!?؟;؛\r\n\u2028\u2029]*(?:[.!?؟]+|[;؛\r\n\u2028\u2029]+|$)/g,
        span => /[?؟]/u.test(span) ? ' '.repeat(span.length) : span);
    // A server-side semantic judgment (TypeSafe) replaces the regex text
    // candidate when present. Floors, Hume fusion, conflict handling and the
    // accessibility label are unchanged: it is a better text witness, not a
    // new decision path. Never runs in the client; the profile arrives fused.
    let semantic = words ? normalizeSemanticTone(semanticTone) : undefined;
    // Reported speech, a quotation, or a question about someone else's
    // feelings is not this speaker's delivery. When the judgment says the
    // words are probably not the speaker's own, the tone abstains the same
    // way the regex path abstains on quoted spans, and the dimensions stay
    // attached for typography that wants them.
    if (semantic && dimensions && typeof dimensions.speakerOwnDelivery === 'number' && dimensions.speakerOwnDelivery < 0.5) {
        semantic = { ...semantic, tone: 'neutral', sentiment: 'neutral' };
    }
    const candidate = semantic
        ? {
            confidence: semantic.tone === 'neutral' ? 0 : clamp01(semantic.confidence * providerWeight(providerConfidence)),
            conflictingSentiment: false,
            score: semantic.confidence,
            sentiment: semantic.tone === 'neutral' ? 'neutral' : semantic.sentiment,
            symbols: symbolsForTone(semantic.tone),
            tone: semantic.tone
        }
        : textCandidate(evidenceWords, providerConfidence, measured);
    const expressions = normalizeHumeExpressions(humeExpressions).flatMap(expression => {
        const entry = findDictionaryEntry(expression.name);
        return entry ? [{ ...entry, humeLabel: expression.name, confidence: expression.score }] : [];
    });
    const hume = expressions[0];
    const humeMeetsFloor = hume && hume.confidence >= Math.max(HUME_FLOOR, hume.confidenceFloor);
    const textMeetsFloor = candidate.confidence >= TEXT_FLOOR;
    const ambiguousAudio = humeMeetsFloor && expressions.some(other => other.tone !== hume.tone && hume.confidence - other.confidence < HUME_MARGIN);
    const conflict = candidate.conflictingSentiment || ambiguousAudio || (humeMeetsFloor && textMeetsFloor && hume.sentiment !== 'neutral'
        && candidate.sentiment !== 'neutral' && hume.sentiment !== candidate.sentiment);
    const useHume = humeMeetsFloor && !conflict && (hume.tone === candidate.tone || !textMeetsFloor || hume.confidence >= candidate.confidence + HUME_MARGIN);
    const useText = !useHume && textMeetsFloor && !conflict;
    const tone = useHume ? hume.tone : useText ? candidate.tone : 'neutral';
    const confidence = conflict ? 0 : useHume ? hume.confidence : candidate.confidence;
    /** @type {import('./caption-tone-analysis').ICaptionToneProfile} */
    const profile = {
        confidence, dictionaryVersion: CAPTION_TONE_DICTIONARY_VERSION,
        fusionDecision: conflict ? 'conflict' : useHume ? 'hume' : useText ? 'text' : 'neutral',
        humeLabel: useHume || conflict ? hume?.humeLabel : undefined,
        intensity: tone === 'neutral' ? 'low' : intensity(confidence), prosody: measured,
        sentiment: tone === 'neutral' ? 'neutral' : useHume ? hume.sentiment : candidate.sentiment,
        source: useHume || (conflict && humeMeetsFloor) ? (words ? 'deepgram-hume-fusion' : 'hume-expression')
            : semantic ? 'semantic-judgment' : measured ? 'mixed-rule' : 'text-rule',
        symbols: tone === 'neutral' ? [] : useHume ? hume.symbols : candidate.symbols,
        tone, visualCues: useHume ? hume.visualCues : undefined
    };
    if (dimensions) profile.dimensions = dimensions;
    profile.accessibilityLabel = getCaptionToneAccessibilityLabel(profile);
    return profile;
}
/** @param {unknown} value @returns {import('./caption-tone-analysis').ICaptionToneProfile | undefined} */
function normalizeCaptionToneProfile(value) {
    if (!isRecord(value) || !isTone(value.tone)) return undefined;
    const confidence = validScore(value.confidence) ? value.confidence : 0;
    const decision = value.fusionDecision;
    const fusionDecision = decision === 'conflict' || decision === 'hume' || decision === 'neutral' || decision === 'text'
        ? decision : undefined;
    const tone = confidence < TEXT_FLOOR || fusionDecision === 'conflict' ? 'neutral' : value.tone;
    const rawSource = value.source;
    const source = rawSource === 'audio-prosody' || rawSource === 'deepgram-hume-fusion'
        || rawSource === 'hume-expression' || rawSource === 'mixed-rule' || rawSource === 'semantic-judgment' ? rawSource : 'text-rule';
    /** @type {import('./caption-tone-analysis').ICaptionToneProfile} */
    const profile = {
        confidence,
        dictionaryVersion: typeof value.dictionaryVersion === 'string' ? value.dictionaryVersion : undefined,
        fusionDecision, humeLabel: typeof value.humeLabel === 'string' ? normalizeExpressionLabel(value.humeLabel) : undefined,
        intensity: tone === 'neutral' ? 'low' : value.intensity === 'high' || value.intensity === 'medium' ? value.intensity : 'low',
        prosody: normalizeProsody(value.prosody), sentiment: tone === 'neutral' ? 'neutral' : sentiment(value.sentiment), source,
        symbols: tone !== 'neutral' && Array.isArray(value.symbols) ? value.symbols.filter(isSymbol).slice(0, 2) : [],
        tone, visualCues: tone !== 'neutral' && Array.isArray(value.visualCues) ? value.visualCues.filter(isVisualCue).slice(0, 5) : undefined
    };
    const dimensions = normalizeCaptionToneDimensions(value.dimensions);
    if (dimensions) profile.dimensions = dimensions;
    // Recompute from validated evidence: stale/forged percentage copy must not
    // override the current abstention and uncertainty policy.
    profile.accessibilityLabel = getCaptionToneAccessibilityLabel(profile);
    return profile;
}

module.exports = { CAPTION_TONE_DICTIONARY_VERSION, analyzeCaptionTone, normalizeCaptionToneProfile, normalizeCaptionToneDimensions,
    getCaptionToneAccessibilityLabel, findDictionaryEntry, normalizeExpressionLabel, normalizeHumeExpressions };
